import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowLeftRight,
  File,
  Inbox,
  Loader2,
  Mail,
  Pencil,
  PenSquare,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import {
  deleteEmailMessage,
  getEmailMessageCounts,
  getEmailMessages,
  restoreEmailMessage,
  starEmailMessage,
  trashEmailMessage,
} from '../../Services/EmailAdminService';
import {
  EmailFolderCounts,
  EmailMessage,
  EmailMessageFolder,
} from '../../Types/EmailAdminType';
import EmailTabNav from './EmailTabNav';
import EmailComposeModal from './EmailComposeModal';

const PAGE_SIZE = 12;

const FOLDERS: { key: EmailMessageFolder; label: string; icon: React.ReactNode }[] = [
  { key: 'ALL', label: 'All Mail', icon: <Inbox size={15} /> },
  { key: 'SENT', label: 'Sent', icon: <Send size={15} /> },
  { key: 'DRAFT', label: 'Drafts', icon: <File size={15} /> },
  { key: 'FAILED', label: 'Failed', icon: <AlertTriangle size={15} /> },
  { key: 'STARRED', label: 'Starred', icon: <Star size={15} /> },
  { key: 'TRASH', label: 'Trash', icon: <Trash2 size={15} /> },
];

const STATUS_STYLES: Record<string, string> = {
  SENT: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  DRAFT: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  FAILED: 'border-red-500/30 bg-red-500/10 text-red-400',
};

const countFor = (counts: EmailFolderCounts | null, folder: EmailMessageFolder) => {
  if (!counts) return undefined;
  switch (folder) {
    case 'ALL':
      return counts.all;
    case 'SENT':
      return counts.sent;
    case 'DRAFT':
      return counts.draft;
    case 'FAILED':
      return counts.failed;
    case 'STARRED':
      return counts.starred;
    case 'TRASH':
      return counts.trash;
    default:
      return undefined;
  }
};

const formatDate = (value: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Wraps a stored body in a complete, explicitly light document before handing it to the iframe.
 * Plain fragments (no html/body, no color-scheme) otherwise fall back to the browser's automatic
 * dark mode and render the message with a black background.
 */
const emailDocument = (body: string) => {
  const trimmed = (body || '').trim();
  if (/^<!doctype/i.test(trimmed) || /^<html/i.test(trimmed)) return trimmed;
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="color-scheme" content="light"><style>html{color-scheme:light}body{margin:0;padding:20px;background:#eef2f7;color:#334155;font-family:Arial,Helvetica,'Segoe UI',sans-serif;}</style></head><body>${trimmed}</body></html>`;
};

function EmailTriggerComponent() {
  const [folder, setFolder] = useState<EmailMessageFolder>('ALL');
  const [messages, setMessages] = useState<EmailMessage[]>([]);
  const [counts, setCounts] = useState<EmailFolderCounts | null>(null);
  const [countsVersion, setCountsVersion] = useState(0);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeDraft, setComposeDraft] = useState<EmailMessage | null>(null);
  const [detail, setDetail] = useState<EmailMessage | null>(null);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const showToast = (variant: AlertProps['variant'], title: string, message: string) =>
    setAlert({ variant, title, message, autoCloseMs: 4000, onClose: () => setAlert(null) });

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEmailMessages({
        folder,
        search: appliedSearch || undefined,
        page,
        size: PAGE_SIZE,
      });
      if (res.success && res.data) {
        const data = res.data as unknown as PaginationResponse<EmailMessage>;
        setMessages(data.content ?? []);
        setTotalPages(data.totalPages ?? 0);
        setTotalElements(data.totalElements ?? 0);
      } else {
        showToast('error', 'Load failed', res.message || 'Could not load messages.');
      }
    } catch {
      showToast('error', 'Load failed', 'Could not load messages.');
    } finally {
      setLoading(false);
    }
  }, [folder, appliedSearch, page]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    getEmailMessageCounts()
      .then((res) => {
        if (res.success && res.data) setCounts(res.data);
      })
      .catch(() => undefined);
  }, [countsVersion, folder, appliedSearch]);

  const refreshAll = () => {
    fetchMessages();
    setCountsVersion((v) => v + 1);
  };

  const switchFolder = (next: EmailMessageFolder) => {
    setFolder(next);
    setPage(0);
  };

  const applySearch = () => {
    setPage(0);
    setAppliedSearch(search.trim());
  };

  const toggleStar = async (msg: EmailMessage) => {
    const res = await starEmailMessage(msg.id, !msg.starred);
    if (res.success) {
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, starred: !msg.starred } : m)));
      setCountsVersion((v) => v + 1);
      if (detail?.id === msg.id) setDetail({ ...detail, starred: !msg.starred });
    }
  };

  const handleTrash = async (msg: EmailMessage) => {
    const res = await trashEmailMessage(msg.id);
    if (res.success) {
      showToast('success', 'Moved to trash', 'The message was moved to trash.');
      refreshAll();
      if (detail?.id === msg.id) setDetail(null);
    } else {
      showToast('error', 'Action failed', res.message || 'Please try again.');
    }
  };

  const handleRestore = async (msg: EmailMessage) => {
    const res = await restoreEmailMessage(msg.id);
    if (res.success) {
      showToast('success', 'Restored', 'The message was restored.');
      refreshAll();
      setDetail(null);
    } else {
      showToast('error', 'Action failed', res.message || 'Please try again.');
    }
  };

  const handleDeleteForever = async (msg: EmailMessage) => {
    if (!window.confirm('Permanently delete this message? This cannot be undone.')) return;
    const res = await deleteEmailMessage(msg.id);
    if (res.success) {
      showToast('success', 'Deleted', 'The message was permanently deleted.');
      refreshAll();
      setDetail(null);
    } else {
      showToast('error', 'Delete failed', res.message || 'Please try again.');
    }
  };

  const openCompose = (draft: EmailMessage | null = null) => {
    setComposeDraft(draft);
    setComposeOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <EmailTabNav />

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <Mail size={22} />
        </span>
        <div className="mr-auto">
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Email Trigger</h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Compose, send and track mail across folders
          </p>
        </div>
        <button
          onClick={refreshAll}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-3 py-2.5 text-xs font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
        <button
          onClick={() => openCompose(null)}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:brightness-110"
        >
          <PenSquare size={14} />
          Compose
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_1fr]">
        {/* Folder rail */}
        <aside className="flex gap-2 overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/50 p-2 lg:flex-col lg:overflow-visible">
          {FOLDERS.map((f) => {
            const active = folder === f.key;
            const count = countFor(counts, f.key);
            return (
              <button
                key={f.key}
                onClick={() => switchFolder(f.key)}
                className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-bold transition ${
                  active
                    ? 'bg-cyan-500/15 text-cyan-400'
                    : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-200'
                }`}
              >
                {f.icon}
                <span className="flex-1 whitespace-nowrap">{f.label}</span>
                {count !== undefined && (
                  <span className={`text-[10px] font-black ${active ? 'text-cyan-400' : 'text-slate-600'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </aside>

        {/* Message list */}
        <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/50">
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 p-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                placeholder="Search subject or recipient"
                className="w-full rounded-xl border border-slate-800 bg-slate-950/60 py-2.5 pl-9 pr-3 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50"
              />
            </div>
            <button
              onClick={applySearch}
              className="rounded-xl border border-cyan-500/40 bg-cyan-500/10 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-cyan-400 transition hover:bg-cyan-500/20"
            >
              Search
            </button>
          </div>

          <div className="divide-y divide-slate-800/60">
            {loading && (
              <div className="flex justify-center py-12">
                <Loader2 className="animate-spin text-cyan-500" />
              </div>
            )}
            {!loading && messages.length === 0 && (
              <div className="px-4 py-12 text-center text-sm font-semibold text-slate-500">
                Nothing here.
              </div>
            )}
            {!loading &&
              messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-3 px-3 py-3 transition hover:bg-slate-900"
                >
                  <button
                    onClick={() => toggleStar(msg)}
                    className={`flex-shrink-0 rounded-lg p-1.5 transition ${
                      msg.starred ? 'text-amber-400' : 'text-slate-600 hover:text-amber-400'
                    }`}
                    title={msg.starred ? 'Unstar' : 'Star'}
                  >
                    <Star size={15} fill={msg.starred ? 'currentColor' : 'none'} />
                  </button>

                  <button onClick={() => setDetail(msg)} className="min-w-0 flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-bold text-slate-100">
                        {msg.toEmails?.join(', ') || '(no recipients)'}
                      </span>
                      <span
                        className={`rounded-md border px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${STATUS_STYLES[msg.status] ?? ''}`}
                      >
                        {msg.status}
                      </span>
                    </div>
                    <p className="truncate text-xs font-semibold text-slate-400">
                      {msg.subject || '(no subject)'}
                    </p>
                    {msg.errorMessage && (
                      <p className="truncate text-[11px] font-semibold text-red-400/80">{msg.errorMessage}</p>
                    )}
                  </button>

                  <span className="hidden flex-shrink-0 text-[11px] font-semibold text-slate-500 sm:block">
                    {formatDate(msg.sentAt || msg.updatedAt || msg.createdAt)}
                  </span>

                  <div className="flex flex-shrink-0 items-center gap-1">
                    {msg.status === 'DRAFT' && folder !== 'TRASH' && (
                      <button
                        onClick={() => openCompose(msg)}
                        className="rounded-lg p-1.5 text-slate-500 transition hover:text-cyan-400"
                        title="Edit draft"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    {folder === 'TRASH' ? (
                      <>
                        <button
                          onClick={() => handleRestore(msg)}
                          className="rounded-lg p-1.5 text-slate-500 transition hover:text-emerald-400"
                          title="Restore"
                        >
                          <RotateCcw size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteForever(msg)}
                          className="rounded-lg p-1.5 text-slate-500 transition hover:text-red-400"
                          title="Delete forever"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleTrash(msg)}
                        className="rounded-lg p-1.5 text-slate-500 transition hover:text-red-400"
                        title="Move to trash"
                      >
                        <ArrowLeftRight size={14} />
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
          </div>

          <div className="flex items-center justify-between border-t border-slate-800 px-4 py-3 text-xs font-semibold text-slate-500">
            <span>
              {totalElements} message{totalElements === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-1.5 font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-40"
              >
                Prev
              </button>
              <span>
                {totalPages === 0 ? 0 : page + 1} / {totalPages}
              </span>
              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-1.5 font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <EmailComposeModal
        open={composeOpen}
        draft={composeDraft}
        onClose={() => setComposeOpen(false)}
        onDone={(m) => {
          showToast('success', 'Done', m);
          refreshAll();
        }}
      />

      {detail && (
        <div className="fixed inset-0 z-[9940] flex items-start justify-center overflow-y-auto bg-slate-950/80 p-4 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
          >
            <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-3">
              <h2 className="min-w-0 flex-1 truncate text-sm font-black text-slate-100">
                {detail.subject || '(no subject)'}
              </h2>
              <span
                className={`rounded-md border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${STATUS_STYLES[detail.status] ?? ''}`}
              >
                {detail.status}
              </span>
              <button
                onClick={() => setDetail(null)}
                className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-slate-100"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-2 border-b border-slate-800 px-4 py-3 text-xs font-semibold text-slate-400">
              <p>
                <span className="text-slate-600">From: </span>
                {detail.fromEmail ? `${detail.fromName ? `${detail.fromName} · ` : ''}${detail.fromEmail}` : 'Default sender'}
              </p>
              <p><span className="text-slate-600">To: </span>{detail.toEmails?.join(', ') || '—'}</p>
              {detail.ccEmails?.length > 0 && <p><span className="text-slate-600">Cc: </span>{detail.ccEmails.join(', ')}</p>}
              {detail.bccEmails?.length > 0 && <p><span className="text-slate-600">Bcc: </span>{detail.bccEmails.join(', ')}</p>}
              <p><span className="text-slate-600">Date: </span>{formatDate(detail.sentAt || detail.updatedAt || detail.createdAt)}</p>
              {detail.templateName && <p><span className="text-slate-600">Template: </span>{detail.templateName}</p>}
              {detail.errorMessage && <p className="text-red-400"><span className="text-slate-600">Error: </span>{detail.errorMessage}</p>}
            </div>

            <div className="max-h-[420px] overflow-y-auto rounded-b-2xl bg-white p-4">
              <iframe
                title="Message body"
                sandbox=""
                srcDoc={emailDocument(detail.body || '')}
                className="min-h-[220px] w-full"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-slate-800 px-4 py-3">
              {detail.status === 'DRAFT' && (
                <button
                  onClick={() => {
                    openCompose(detail);
                    setDetail(null);
                  }}
                  className="inline-flex items-center gap-2 rounded-xl border border-cyan-500/40 bg-cyan-500/10 px-4 py-2.5 text-sm font-black text-cyan-400 transition hover:bg-cyan-500/20"
                >
                  <Pencil size={15} /> Edit draft
                </button>
              )}
              <button
                onClick={() => toggleStar(detail)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-2.5 text-sm font-black text-slate-300 transition hover:border-amber-500/40 hover:text-amber-400"
              >
                <Star size={15} fill={detail.starred ? 'currentColor' : 'none'} />
                {detail.starred ? 'Unstar' : 'Star'}
              </button>
              <div className="ml-auto flex gap-2">
                {folder === 'TRASH' ? (
                  <>
                    <button
                      onClick={() => handleRestore(detail)}
                      className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-2.5 text-sm font-black text-emerald-400 transition hover:bg-emerald-500/20"
                    >
                      <RotateCcw size={15} /> Restore
                    </button>
                    <button
                      onClick={() => handleDeleteForever(detail)}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm font-black text-red-400 transition hover:bg-red-500/20"
                    >
                      <Trash2 size={15} /> Delete
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleTrash(detail)}
                    className="inline-flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm font-black text-red-400 transition hover:bg-red-500/20"
                  >
                    <Trash2 size={15} /> Move to trash
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {alert && <Alert {...alert} onClose={() => setAlert(null)} />}
    </div>
  );
}

export default EmailTriggerComponent;
